import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { GetTeams, GetTeamsResponse, CompanyTeam } from '../../apis/settings';

export interface TeamsState {
  list: CompanyTeam[];
  loading: boolean;
  error: string | null;
  hasFetched: boolean;
}

interface RootState {
  teams: TeamsState;
}

export const fetchTeamsDDL = createAsyncThunk<
  CompanyTeam[],
  string,
  { rejectValue: string }
>('teams/fetchTeamsDDL', async (token: string, { rejectWithValue }) => {
  try {
    const res: GetTeamsResponse = await GetTeams(token);
    const data = res?.data;

    if (data?.data?.is_success) {
      return data.data.data ?? [];
    } else {
      return rejectWithValue(data?.data?.message || 'Failed to fetch teams');
    }
  } catch {
    return rejectWithValue('Error fetching teams');
  }
});

const initialState: TeamsState = {
  list: [],
  loading: false,
  error: null,
  hasFetched: false,
};

/**
 * Redux slice for teams state management
 * Handles fetching and storing teams data from the API
 */
const teamSlice = createSlice({
  name: 'teams',
  initialState,
  reducers: {
    clearTeams: (state) => {
      state.list = [];
      state.loading = false;
      state.error = null;
      state.hasFetched = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTeamsDDL.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.hasFetched = true;
      })
      .addCase(fetchTeamsDDL.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
      })
      .addCase(fetchTeamsDDL.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
        state.hasFetched = false;
      });
  },
});

export const { clearTeams } = teamSlice.actions;

// Selector to get teams state
export const selectTeamsState = (state: RootState): TeamsState => state.teams;

export const teamReducer = teamSlice.reducer;
